import React, { useState } from 'react';
import { ChevronDown, Settings, LogOut, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { UserRole } from '@/types';

const UserDropdown: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);

  if (!user) return null;

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getRoleLabel = (role: string) => {
    const roleLabels = {
      'SUPERADMIN': 'Super Administrator',
      'ADMIN': 'Administrator',
      'VIEWER': 'Betrachter',
      'USER': 'Benutzer',
    };
    return roleLabels[role as keyof typeof roleLabels] || role;
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 h-8 px-2"
      >
        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center">
          {getInitials(user.name)}
        </div>
        <span className="hidden md:inline text-sm font-medium">
          {user.name}
        </span>
        <ChevronDown className="h-3 w-3" />
      </Button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-56 bg-background border rounded-md shadow-lg z-50">
          <div className="py-2">
            {/* User Info */}
            <div className="px-3 py-2 border-b">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center">
                  {getInitials(user.name)}
                </div>
                <div>
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-xs bg-secondary px-2 py-1 rounded">
                  {getRoleLabel(user.role)}
                </span>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              {/* Einstellungen nur für Superadmins */}
              {user.role === UserRole.SUPERADMIN && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    // Navigate to settings
                    window.location.href = '/settings';
                  }}
                  className="w-full flex items-center space-x-2 px-3 py-2 text-sm hover:bg-muted"
                >
                  <Settings className="h-4 w-4" />
                  <span>Einstellungen</span>
                </button>
              )}
              
              <button
                onClick={() => {
                  toggleTheme();
                  setIsOpen(false);
                }}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm hover:bg-muted"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
                <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm hover:bg-muted text-destructive"
              >
                <LogOut className="h-4 w-4" />
                <span>Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default UserDropdown;
